import { ApolloError } from "@apollo/client";
import classNames from "classnames";
import React, {
    HTMLAttributes,
    JSX,
    ReactElement, ReactNode, useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    ErrorRemoteView,
    ErrorSecurityView,
    ErrorView,
} from "../../../pages/error/Error";
import useOrCreateRef from "../../useOrCreateRef";
import Button from "../button/Button";
import { SettingsContainerContext, SettingsContext } from "./base";
import Setting from "./Setting";
import Styles from "./Settings.module.scss";
import SettingsSection from "./SettingsSection";
import SettingsText from "./Text";
import Badge from "../badge/Badge";

type AsProp = keyof JSX.IntrinsicElements;

export interface SettingsProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    title?: string;
    helpText?: string;
    navigation?: "sections" | "tabs" | "none" | "progress-bar";
    navigationButtons?: ReactNode;
    selectedSectionKey?: string;
    onSectionChanged?: (key: string) => void;
    saveElement?: ReactNode;
    error?: Error;
    as?: AsProp;
}

const SETTING_ID = "sectionId";

function getScrollParent(element: HTMLElement): HTMLElement | undefined {
    if (element === document.body) {
        return undefined;
    }

    const style = window.getComputedStyle(element);

    if (style.overflowY === "scroll"
        || style.overflowY === "auto") {
        return element;
    }

    return element.parentElement ? getScrollParent(element.parentElement) : undefined;
}

const SettingsContainer = React.forwardRef((
    args: SettingsProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const {
        children,
        className,
        navigation,
        navigationButtons,
        selectedSectionKey,
        as: asType,
        saveElement,
        error,
        onSectionChanged,
        ...rest
    } = args;

    const isTabs = navigation === "tabs" || navigation === "progress-bar";

    const reference = useOrCreateRef(ref);
    const container = useContext(SettingsContext).useContainer();

    const [currentTab, setCurrentTab] = container.useTabSelection();

    useEffect(() => {
        if (isTabs) {
            if (container.sections.isNotEmpty) {
                const selected = selectedSectionKey
                    ? container.sections.find(section => section.sectionKey === selectedSectionKey)
                    : undefined;
                setCurrentTab(selected ?? container.sections.firstElement);
            }
            return;
        }
        if (navigation !== "sections") {
            return;
        }
        const intersection = new IntersectionObserver(elements => {
            elements.forEach(element => {
                const id = (element.target as HTMLDivElement).dataset[SETTING_ID];
                const button = reference.current?.querySelector(`[data-button-section-id='${id}']`);
                if (element.isIntersecting) {
                    button?.classList.add(Styles["button-hover"]);
                } else {
                    button?.classList.remove(Styles["button-hover"]);
                }
            });
        });
        container.sections
            .forEach(section => {
                const element = section.reference.current;
                if (!element) {
                    return;
                }

                element.dataset[SETTING_ID] = section.id;
                intersection.observe(element);
            });
        // eslint-disable-next-line consistent-return
        return () => {
            intersection.disconnect();
        };
    }, [container.sections.length, navigation, selectedSectionKey]);

    const [isShowMenu, setShowMenu] = useState(true);
    const lastScrollPositionRef = useRef(0);

    useEffect(() => {
        const parent = navigation === "sections" && reference.current ? getScrollParent(reference.current) : undefined;
        if (!parent) {
            return;
        }
        const scrollHandler = () => {
            const scrollTop = parent.scrollTop + parent.clientTop;
            if (scrollTop > lastScrollPositionRef.current && scrollTop > window.innerHeight / 2) {
                setShowMenu(false);
            } else {
                setShowMenu(true);
            }

            lastScrollPositionRef.current = scrollTop;
        };
        parent?.addEventListener("scroll", scrollHandler);
        // eslint-disable-next-line consistent-return
        return () => {
            parent?.removeEventListener("scroll", scrollHandler);
        };
    }, [container, reference.current, navigation]);

    const buttons = container.sections.mapFilter((section, index) => {
        const button = ((section.canDisplay || isTabs) ? (
            <Button
                type="button"
                size="small"
                className={currentTab === section ? Styles["button-hover"] : ""}
                variant={navigation === "tabs" ? "ghost-gray" : "none"}
                prefixIcon={navigation === "progress-bar"
                    ? <Badge isSelected={currentTab === section} variant="counter">{index + 1}</Badge>
                    : undefined}
                onClick={() => {
                    if (isTabs) {
                        if (section.sectionKey) {
                            try {
                                onSectionChanged?.(section.sectionKey);
                            } catch (err) {
                                return;
                            }
                        }
                        setCurrentTab(section);
                    } else {
                        section.reference.current?.scrollIntoView({ behavior: "smooth" });
                    }
                }}
                data-button-section-id={section.id}
            >
                {section.title}
                {" "}
                {section.counter != null && (
                    <Badge
                        className={Styles["navigation-counter"]}
                        variant="counter"
                        size="small"
                        isSelected={currentTab === section}
                    >
                        {section.counter}
                    </Badge>
                )}
            </Button>
        ) : undefined);

        return button;
    });

    const ParentComponent = (asType ?? "div") as React.ElementType;

    return (
        <SettingsContainerContext.Provider value={container}>
            <ParentComponent className={classNames(Styles.container, className)} ref={reference} {...rest}>
                <div className={classNames(Styles.sections)}>
                    {navigation !== "none" && buttons.isNotEmpty && (

                        <div className={Styles["button-list-container"]}>
                            <div className={classNames(
                                Styles["button-list"],
                                Styles[`button-list-${isShowMenu ? "show" : "hide"}`],
                                Styles[`navigation-${navigation}`],
                            )}
                            >
                                {buttons}
                            </div>
                            <div className={Styles["button-list-navigation"]}>
                                {navigationButtons}
                            </div>
                        </div>
                    )}

                    {container.sections.isNotEmpty && buttons.isEmpty && (
                        <ErrorSecurityView
                            title="No Permissions"
                            message="Seems you got to this section but you do not have permissions"
                        />
                    )}
                    {error
                        ? (
                            <div className={Styles.section}>
                                {error instanceof ApolloError ? (
                                    <ErrorRemoteView error={error} />
                                ) : (
                                    <ErrorView message={error.message} title="Error Occurred" />
                                )}
                            </div>
                        )
                        : (children)}
                </div>
                {saveElement}
            </ParentComponent>
        </SettingsContainerContext.Provider>
    );
});

SettingsContainer.defaultProps = {
    navigation: "sections",
    as: "form",
    saveElement: undefined,
    error: undefined,
    navigationButtons: undefined,
};

type SettingsWithTypes = typeof SettingsContainer & {
    Section: typeof SettingsSection;
    Setting: typeof Setting;
    Text: typeof SettingsText
}

const Settings = SettingsContainer as unknown as SettingsWithTypes;
Settings.Section = SettingsSection;
Settings.Setting = Setting;
Settings.Text = SettingsText;

export default Settings;
